import React from 'react';
import PropTypes from 'prop-types';

// API
import {useLazyGetResourcesDataQuery, useLazyGetResourcesDataNextPageQuery} from 'api';

// MUI components
import {Typography} from '@mui/material';
import Alert from '@mui/material/Alert';

// Custom components
import AccordionTable from 'components/accordion-table';

// Constants and utils
import {sortByFieldName} from './utils';

// REDUX
import {connect} from 'react-redux';
import {setResourcesData, resourcesDataSelector} from 'redux/slices/searchResultsSlice';

const PatientResources = ({
  resources,
  patientId,
  patientRestriction,
  resourcesDataFromRedux,
  setSearchResultsResourcesData
}) => {
  const [getResourcesData] = useLazyGetResourcesDataQuery();
  const [getResourcesDataNextPage] = useLazyGetResourcesDataNextPageQuery();

  const sortedResources = resources ? sortByFieldName(resources, 'resourceType') : [];

  const getResource = (resourceType) => {
    setSearchResultsResourcesData({
      ...resourcesDataFromRedux,
      [patientId]: {...resourcesDataFromRedux[patientId], [resourceType]: {resources: null, isLoading: true}}
    });

    getResourcesData({patientId, resourceType}).then((resourcesData) => {
      if (!resourcesData.isError) {
        setSearchResultsResourcesData({
          ...resourcesDataFromRedux,
          [patientId]: {
            ...resourcesDataFromRedux[patientId],
            [resourceType]: {
              resources: resourcesData.data.resources,
              nextPageLink: resourcesData.data.nextPageLink,
              isLoading: false
            }
          }
        });
      } else if (resourcesData.isError) {
        setSearchResultsResourcesData({
          ...resourcesDataFromRedux,
          [patientId]: {
            ...resourcesDataFromRedux[patientId],
            [resourceType]: {resources: null, isLoading: false, isError: true}
          }
        });
      }
    });
  };

  const loadMoreResources = (resourceType, nextPageLink) => {
    getResourcesDataNextPage(nextPageLink).then((resourcesDataNextPage) => {
      setSearchResultsResourcesData({
        ...resourcesDataFromRedux,
        [patientId]: {
          ...resourcesDataFromRedux[patientId],
          [resourceType]: {resources: resourcesDataFromRedux[patientId]?.[resourceType].resources, isLoading: true}
        }
      });
      if (!resourcesDataNextPage.isError) {
        const resourcesToAdd = resourcesDataFromRedux[patientId]?.[resourceType].resources.concat(
          resourcesDataNextPage.data.resources
        );
        setSearchResultsResourcesData({
          ...resourcesDataFromRedux,
          [patientId]: {
            ...resourcesDataFromRedux[patientId],
            [resourceType]: {
              resources: resourcesToAdd,
              nextPageLink: resourcesDataNextPage.nextPageLink || '',
              isLoading: false
            }
          }
        });
      } else if (resourcesDataNextPage.isError) {
        setSearchResultsResourcesData({
          ...resourcesDataFromRedux,
          [patientId]: {
            ...resourcesDataFromRedux[patientId],
            [resourceType]: {
              resources: resourcesDataFromRedux[patientId]?.[resourceType].resources,
              nextPageLink: resourcesDataFromRedux[patientId]?.[resourceType].nextPageLink,
              isLoading: false,
              isError: true
            }
          }
        });
      }
    });
  };

  return (
    <>
      {resources?.length > 0 && <Typography variant="overline">Resources</Typography>}
      {resources?.length > 0 &&
        sortedResources.map((resource, index) =>
          resource.inError ? (
            <Alert key={`inError-${index}`} style={{marginTop: 0, marginBottom: 24}} severity="error">
              {resource.resourceType} data could not be retrieved
            </Alert>
          ) : (
            <AccordionTable
              key={`${index}-patient-resource-${resource}`}
              accordionHeader={resource.resourceType}
              getTableData={() => getResource(resource.resourceType)}
              tableData={resourcesDataFromRedux[patientId]?.[resource.resourceType]?.resources || null}
              loadMore={() =>
                loadMoreResources(
                  resource.resourceType,
                  resourcesDataFromRedux[patientId]?.[resource.resourceType]?.nextPageLink
                )
              }
              shouldShowLoadButton={Boolean(
                resourcesDataFromRedux[patientId]?.[resource.resourceType]?.nextPageLink ||
                  resourcesDataFromRedux[patientId]?.[resource.resourceType]?.isError
              )}
              isLoading={resourcesDataFromRedux[patientId]?.[resource.resourceType]?.isLoading}
              isError={resourcesDataFromRedux[patientId]?.[resource.resourceType]?.isError}
              fieldNames={[
                {key: 'id', value: 'Resource ID'},
                {key: 'isRestricted', value: 'Access restrictions'},
                {key: 'restrictions', value: 'Restriction Details'}
              ]}
              badgeCount={resource.count}
              showBadge
              globalRestriction={patientRestriction}
            >
              {resourcesDataFromRedux[patientId]?.[resource.resourceType]?.resources?.length < 1 ? (
                <Typography variant="body2">This patient has no resources of type {resource.resourceType}</Typography>
              ) : (
                <></>
              )}
            </AccordionTable>
          )
        )}
    </>
  );
};

PatientResources.propTypes = {
  resources: PropTypes.array,
  patientId: PropTypes.string,
  patientRestriction: PropTypes.object,
  resourcesDataFromRedux: PropTypes.object,
  setSearchResultsResourcesData: PropTypes.func
};

const mapStateToProps = (state) => ({
  resourcesDataFromRedux: resourcesDataSelector(state)
});

const mapDispatchToProps = (dispatch) => {
  return {
    setSearchResultsResourcesData: (data) => dispatch(setResourcesData(data))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PatientResources);
